import PropTypes from "prop-types";
import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { LockClosedIcon } from "@heroicons/react/outline";
import {
  useComplianceContext,
  flattenTasks,
  buildDefaultCategories,
} from "context/ComplianceContext";
import { getCorporatesForDashboard } from "state/slices/corporatesForDashboard";
import complianceService from "services/compliance";
import ComplianceSummaryGrid from "./ComplianceSummaryGrid";
import ComplianceTrackerTable from "./ComplianceTrackerTable";
import TemplatesTable from "./TemplatesTable";
import SanctionListManager from "./SanctionListManager";
import StatusBadge from "./StatusBadge";
import OrgComplianceTasks from "./OrgComplianceTasks";
import ComplianceTaskSettings from "./ComplianceTaskSettings";
import {
  formatDateDisplay,
  getTaskStatusBreakdown,
  computeSummaryFromTasks,
} from "./utils";
const ALLOWED_VIEWS = new Set(["summary", "tracker", "analysis", "settings"]);
const roundToTwo = (value) => Math.round(value * 100) / 100;
const normalizeString = (value) => {
  if (value === undefined || value === null) {
    return "";
  }
  if (Array.isArray(value)) {
    return value
      .map((item) => normalizeString(item))
      .filter(Boolean)
      .join(" ")
      .trim();
  }
  return String(value).trim();
};
const buildEmptySummary = () => ({
  compliant: 0,
  notDue: 0,
  due: 0,
  notCompliant: 0,
});
const buildTrackerEntities = (
  userProfile,
  corporateSummaries,
  companySummaries,
  listCorporateTasks,
  listCompanyTasks,
  associatedCompanies = [],
  template
) => {
  const corporateEntities = (userProfile.corporateIds || []).map(
    (corporateId) => {
      const corporateSummary =
        corporateSummaries.find((item) => item.id === corporateId) || {};
      const sourceCompany =
        associatedCompanies.find(
          (company) => String(company._id) === String(corporateId)
        ) || {};
      const annualReturnDate =
        sourceCompany.annualReturnDate || sourceCompany?.registrationDate;
      let tasks = listCorporateTasks(corporateId);
      // Fallback for context entities with no tasks
      if (!tasks || tasks.length === 0) {
        const fallbackCategories =
          template?.categories ||
          buildDefaultCategories(new Date().getFullYear());
        tasks = flattenTasks({
          id: corporateId,
          categories: fallbackCategories,
        });
      }
      return {
        id: corporateId,
        name:
          corporateSummary.registrationName ||
          corporateSummary.name ||
          corporateId,
        label: "Corporate",
        tasks: tasks,
        companySummary: corporateSummary,
        promptMonth:
          annualReturnDate || corporateSummary.registrationDate || null,
        annualReturnDate,
      };
    }
  );
  const companyEntities = (userProfile.companyIds || []).map((companyId) => {
    const companySummary =
      companySummaries.find((item) => item.id === companyId) || {};
    const sourceCompany =
      associatedCompanies.find(
        (company) => String(company._id) === String(companyId)
      ) || {};
    const annualReturnDate =
      sourceCompany.annualReturnDate ||
      companySummary.annualReturnDate ||
      companySummary.registrationDate ||
      null;
    let tasks = listCompanyTasks(companyId);
    // Fallback for context entities with no tasks
    if (!tasks || tasks.length === 0) {
      const fallbackCategories =
        template?.categories ||
        buildDefaultCategories(new Date().getFullYear());
      tasks = flattenTasks({ id: companyId, categories: fallbackCategories });
    }
    return {
      id: companyId,
      name: companySummary.registrationName || companySummary.name || companyId,
      label: "Company",
      tasks: tasks,
      promptMonth: annualReturnDate || companySummary.registrationDate || null,
      annualReturnDate,
    };
  });
  // Add associated companies from Redux state if not already present
  const associatedEntities = associatedCompanies
    .filter(
      (company) =>
        !userProfile.corporateIds?.includes(company._id) &&
        !userProfile.companyIds?.includes(company._id)
    )
    .map((company) => {
      const companyId = company._id;
      // Check if this company has its own compliance summary in context
      const companySummary =
        companySummaries.find((item) => item.id === companyId) ||
        corporateSummaries.find((item) => item.id === companyId) ||
        {};
      // Get tasks from context based on its type, or fallback to flattening the company object itself
      let tasks = company.partnershipType
        ? listCorporateTasks(companyId)
        : listCompanyTasks(companyId);
      // Fallback: if context has no tasks but company object has categories, flatten them
      if (!tasks || tasks.length === 0) {
        if (company.categories) {
          tasks = flattenTasks(company);
        } else {
          // Final fallback: use template categories or default if none exist
          const fallbackCategories =
            template?.categories ||
            buildDefaultCategories(new Date().getFullYear());
          tasks = flattenTasks({ ...company, categories: fallbackCategories });
        }
      }
      return {
        id: companyId,
        name:
          company.registrationName ||
          company.names?.registrationName ||
          company.names?.companyName ||
          company.name ||
          companySummary.registrationName ||
          companySummary.name ||
          companyId,
        label: company.partnershipType ? "Corporate" : "Company",
        tasks: tasks,
        promptMonth:
          company.annualReturnDate || companySummary.registrationDate || null,
        annualReturnDate: company.annualReturnDate,
      };
    });
  return [...corporateEntities, ...companyEntities, ...associatedEntities];
};
const SummaryView = ({ userProfile, template, history, trackerEntities }) => {
  const categories = template?.categories || [];
  const allTasks = React.useMemo(
    () => (trackerEntities || []).flatMap((e) => e.tasks || []),
    [trackerEntities]
  );
  const mandatoryTasks = React.useMemo(
    () => (allTasks || []).filter((t) => t && t.isMandatory === true),
    [allTasks]
  );
  const mandatoryCounts = React.useMemo(() => {
    return (mandatoryTasks || []).reduce(
      (acc, t) => {
        acc.total += 1;
        const k = t.summaryStatus;
        if (k === "compliant") acc.compliant += 1;
        else if (k === "notDue") acc.notDue += 1;
        else if (k === "due") acc.due += 1;
        else if (k === "notCompliant") acc.notCompliant += 1;
        return acc;
      },
      { total: 0, compliant: 0, notDue: 0, due: 0, notCompliant: 0 }
    );
  }, [mandatoryTasks]);
  const mandatorySummary = React.useMemo(() => {
    const total = mandatoryCounts.total || 0;
    if (total === 0) {
      return { compliant: 0, notDue: 0, due: 0, notCompliant: 0 };
    }
    const p = (v) => Math.round((v / total) * 100 * 100) / 100;
    return {
      compliant: p(mandatoryCounts.compliant),
      notDue: p(mandatoryCounts.notDue),
      due: p(mandatoryCounts.due),
      notCompliant: p(mandatoryCounts.notCompliant),
    };
  }, [mandatoryCounts]);
  return (
    <div className="space-y-6">
      <ComplianceSummaryGrid
        summary={mandatorySummary}
        counts={{
          compliant: mandatoryCounts.compliant,
          notDue: mandatoryCounts.notDue,
          due: mandatoryCounts.due,
          notCompliant: mandatoryCounts.notCompliant,
        }}
        caption="Percentages are based on mandatory tasks across all companies"
      />
      <div className="space-y-4 rounded-xl border border-gray-200 p-4">
        <div className="flex flex-col gap-2 md:flex-row md:items-center md:justify-between">
          <div>
            <h3 className="text-lg font-semibold text-gray-800">
              Template Overview
            </h3>
            <p className="text-sm text-gray-600">
              View your compliance template categories and tasks.
            </p>
          </div>
        </div>
        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-200 text-left text-sm">
            <thead className="bg-gray-50 text-xs uppercase tracking-wide text-gray-500">
              <tr>
                <th className="px-4 py-2">Category</th>
                <th className="px-4 py-2">Tasks</th>
                <th className="px-4 py-2 text-right">Weight %</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100">
              {categories.length === 0 ? (
                <tr>
                  <td className="px-4 py-3 text-sm text-gray-500" colSpan={3}>
                    No categories captured yet.
                  </td>
                </tr>
              ) : (
                categories.map((category) => {
                  const directTasks = Array.isArray(category.tasks)
                    ? category.tasks
                    : [];
                  const sectionTasks = Array.isArray(category.sections)
                    ? category.sections.flatMap((s) => s?.tasks || [])
                    : [];
                  const allTasks = [...directTasks, ...sectionTasks];
                  const activeCount = allTasks.filter(
                    (task) =>
                      task &&
                      (!task.lifecycleStatus ||
                        task.lifecycleStatus === "active")
                  ).length;
                  return (
                    <tr key={category.id}>
                      <td className="px-4 py-2 font-medium text-gray-700">
                        {category.name}
                      </td>
                      <td className="px-4 py-2 text-gray-600">{activeCount}</td>
                      <td className="px-4 py-2 text-right text-gray-600">
                        {Number(category.weight || 0).toFixed(2)}%
                      </td>
                    </tr>
                  );
                })
              )}
            </tbody>
          </table>
        </div>
      </div>
      <div className="space-y-3 rounded-xl border border-gray-200 p-4">
        <h3 className="text-lg font-semibold text-gray-800">
          Template History
        </h3>
        <p className="text-sm text-gray-600">
          Historical view of template assignments.
        </p>
        <ul className="space-y-3">
          {history.length === 0 ? (
            <li className="text-sm text-gray-500">
              No template history recorded.
            </li>
          ) : (
            history.map((entry) => (
              <li key={entry.id} className="flex items-start gap-3">
                <span
                  className="mt-1 h-2 w-2 rounded-full bg-sflPrimary"
                  aria-hidden="true"
                />
                <div>
                  <p className="text-sm font-semibold text-gray-700">
                    {entry.templateName} v{entry.version}
                  </p>
                  <p className="text-xs text-gray-500">
                    Applied {formatDateDisplay(entry.appliedAt)} by{" "}
                    {entry.appliedBy} (
                    {entry.origin === "superAdmin" ? "SuperAdmin" : "OrgAdmin"})
                    {entry.note ? ` — ${entry.note}` : ""}
                  </p>
                </div>
              </li>
            ))
          )}
        </ul>
      </div>
    </div>
  );
};
SummaryView.propTypes = {
  userProfile: PropTypes.object.isRequired,
  template: PropTypes.object,
  history: PropTypes.array.isRequired,
};
SummaryView.defaultProps = {
  template: undefined,
};
const TemplateEditor = ({
  template,
  frequencyOptions,
  timelineTypes,
  onUpdateCategory,
  onAddCategory,
  onRemoveCategory,
  onUpdateTask,
  onAddTask,
  onRemoveTask,
}) => {
  const [expandedCategoryId, setExpandedCategoryId] = useState(null);
  const [hasManualCategorySelection, setHasManualCategorySelection] =
    useState(false);
  const [expandedTask, setExpandedTask] = useState(null);
  useEffect(() => {
    if (!template.categories?.length) {
      setExpandedCategoryId(null);
      setHasManualCategorySelection(false);
      return;
    }
    const categories = template.categories;
    const categoryExists =
      expandedCategoryId &&
      categories.some((category) => category.id === expandedCategoryId);
    if (expandedCategoryId && !categoryExists) {
      setExpandedCategoryId(null);
      setHasManualCategorySelection(false);
      return;
    }
  }, [template, expandedCategoryId, hasManualCategorySelection]);
  useEffect(() => {
    if (!expandedTask) {
      return;
    }
    const normalizedCategoryId = normalizeString(expandedTask.categoryId);
    const normalizedTaskId = normalizeString(expandedTask.taskId);
    if (!normalizedCategoryId || !normalizedTaskId) {
      setExpandedTask(null);
      return;
    }
    const category = template.categories.find(
      (candidate) => normalizeString(candidate.id) === normalizedCategoryId
    );
    if (!category) {
      setExpandedTask(null);
      return;
    }
    const taskExists = (category.tasks || []).some(
      (task) =>
        task.lifecycleStatus !== "delete" &&
        normalizeString(task.id) === normalizedTaskId
    );
    if (!taskExists) {
      setExpandedTask(null);
    }
  }, [template, expandedTask]);
  useEffect(() => {
    setExpandedTask(null);
  }, [expandedCategoryId]);
  const timelineOptions = [
    { value: timelineTypes.DEFINITE, label: "Definite" },
    { value: timelineTypes.INDEFINITE, label: "Indefinite" },
  ];
  const handleAddCategory = () => {
    const newCategoryId = onAddCategory(template.id);
    if (newCategoryId) {
      setExpandedCategoryId(newCategoryId);
      setHasManualCategorySelection(true);
    }
  };
  const handleToggleTask = useCallback((categoryId, taskId) => {
    const normalizedCategoryId = normalizeString(categoryId);
    const normalizedTaskId = normalizeString(taskId);
    if (!normalizedCategoryId || !normalizedTaskId) {
      return;
    }
    setExpandedTask((previous) => {
      if (
        previous &&
        previous.categoryId === normalizedCategoryId &&
        previous.taskId === normalizedTaskId
      ) {
        return null;
      }
      return { categoryId: normalizedCategoryId, taskId: normalizedTaskId };
    });
  }, []);
  const handleAddTask = useCallback(
    (categoryId) => {
      onAddTask(template.id, categoryId, {
        title: "New Compliance Task",
      });
    },
    [onAddTask, template.id]
  );
  return (
    <section className="space-y-5">
      <header className="flex flex-col gap-2 md:flex-row md:items-center md:justify-between">
        <div>
          <p className="text-sm text-gray-600">
            Update categories and underlying tasks before assigning to
            corporates.
          </p>
        </div>
        <button
          type="button"
          onClick={handleAddCategory}
          className="inline-flex items-center justify-center rounded-md border border-sflPrimary px-4 py-2 text-sm font-semibold text-sflPrimary transition hover:bg-sflPrimary/10 focus:outline-none focus:ring-2 focus:ring-sflPrimary focus:ring-offset-2"
        >
          + Add Category
        </button>
      </header>
      <div className="space-y-4">
        {(template.categories || []).map((category) => {
          const isOpen = expandedCategoryId === category.id;
          const visibleTasks = (category.tasks || []).filter(
            (task) => task.lifecycleStatus !== "delete"
          );
          const normalizedCategoryId = normalizeString(category.id);
          const hasSuperAdminMandatoryTask = (category.tasks || []).some(
            (task) => task.isMandatory && task.mandatoryBy === "superAdmin"
          );
          return (
            <div
              key={category.id}
              className="rounded-2xl border border-gray-200"
            >
              <button
                type="button"
                onClick={() => {
                  setHasManualCategorySelection(true);
                  setExpandedCategoryId(isOpen ? null : category.id);
                }}
                className="flex w-full items-center justify-between gap-3 px-5 py-4 text-left"
              >
                <div>
                  <p className="text-base font-semibold text-gray-800">
                    {category.name}
                  </p>
                  {category.description && (
                    <p className="text-sm text-gray-600">
                      {category.description}
                    </p>
                  )}
                </div>
                <div className="text-xs font-semibold text-gray-500">
                  {visibleTasks.length} task
                  {visibleTasks.length === 1 ? "" : "s"}
                </div>
              </button>
              {isOpen && (
                <div className="space-y-6 border-t border-gray-200 p-5">
                  <div className="grid gap-4 md:grid-cols-3">
                    <div className="md:col-span-2">
                      <label className="text-xs font-semibold uppercase tracking-wide text-gray-500">
                        Category Name
                      </label>
                      <input
                        type="text"
                        value={category.name}
                        onChange={(event) =>
                          onUpdateCategory(template.id, category.id, {
                            name: event.target.value,
                          })
                        }
                        disabled={hasSuperAdminMandatoryTask}
                        className="mt-1 w-full rounded-md border border-gray-200 px-3 py-2 text-sm focus:border-sflPrimary focus:outline-none disabled:bg-gray-50 disabled:text-gray-400"
                      />
                    </div>
                    <div>
                      <label className="text-xs font-semibold uppercase tracking-wide text-gray-500">
                        Weight (%)
                      </label>
                      <input
                        type="number"
                        min="0"
                        max="100"
                        step="0.01"
                        value={category.weight ?? 0}
                        onChange={(event) =>
                          onUpdateCategory(template.id, category.id, {
                            weight: event.target.value,
                          })
                        }
                        disabled={hasSuperAdminMandatoryTask}
                        className="mt-1 w-full rounded-md border border-gray-200 px-3 py-2 text-sm focus:border-sflPrimary focus:outline-none disabled:bg-gray-50 disabled:text-gray-400"
                      />
                    </div>
                    <div className="md:col-span-3">
                      <label className="text-xs font-semibold uppercase tracking-wide text-gray-500">
                        Description
                      </label>
                      <textarea
                        value={category.description || ""}
                        onChange={(event) =>
                          onUpdateCategory(template.id, category.id, {
                            description: event.target.value,
                          })
                        }
                        rows={3}
                        disabled={hasSuperAdminMandatoryTask}
                        className="mt-1 w-full rounded-md border border-gray-200 px-3 py-2 text-sm focus:border-sflPrimary focus:outline-none disabled:bg-gray-50 disabled:text-gray-400"
                      />
                    </div>
                  </div>
                  <div className="space-y-4">
                    {visibleTasks.length === 0 ? (
                      <div className="rounded-lg border border-dashed border-gray-300 p-4 text-sm text-gray-500">
                        No tasks configured yet.
                      </div>
                    ) : (
                      visibleTasks.map((task) => {
                        const normalizedTaskId = normalizeString(task.id);
                        const isTaskOpen =
                          expandedTask?.categoryId === normalizedCategoryId &&
                          expandedTask?.taskId === normalizedTaskId;
                        const timelineType =
                          task.timelineType || timelineTypes.DEFINITE;
                        const timelineLabel =
                          timelineOptions.find(
                            (option) => option.value === timelineType
                          )?.label || "Definite";
                        const frequencyLabel = frequencyOptions.find(
                          (option) => option.value === task.frequency
                        )?.label;
                        const headerMetaParts = [`${timelineLabel} timeline`];
                        const isSuperAdminMandatoryTask =
                          task.isMandatory && task.mandatoryBy === "superAdmin";
                        if (timelineType === timelineTypes.INDEFINITE) {
                          if (frequencyLabel) {
                            headerMetaParts.push(
                              `Repeats ${frequencyLabel.toLowerCase()}`
                            );
                          }
                        } else {
                          headerMetaParts.push(
                            task.deadline
                              ? `Due ${task.deadline}`
                              : "No deadline set"
                          );
                        }
                        if (task.startDate) {
                          headerMetaParts.push(`Starts ${task.startDate}`);
                        }
                        const headerMeta = headerMetaParts.join(" • ");
                        return (
                          <div
                            key={task.id}
                            className="rounded-xl border border-gray-200 p-4 shadow-sm"
                          >
                            <div className="flex flex-col gap-3 md:flex-row md:items-center md:justify-between">
                              <button
                                type="button"
                                onClick={() =>
                                  handleToggleTask(category.id, task.id)
                                }
                                className="flex flex-1 items-center justify-between gap-3 text-left"
                                aria-expanded={isTaskOpen}
                              >
                                <div className="min-w-0">
                                  <div className="flex flex-wrap items-center gap-2">
                                    <p className="text-sm font-semibold text-gray-800">
                                      {task.title || "Untitled Task"}
                                    </p>
                                    {task.isMandatory && (
                                      <span className="inline-flex items-center gap-1 rounded-full border border-sflPrimary/40 bg-sflPrimary/10 px-2.5 py-0.5 text-[0.6875rem] font-semibold text-sflPrimary">
                                        <LockClosedIcon
                                          className="h-3.5 w-3.5"
                                          aria-hidden="true"
                                        />
                                        <span>Mandatory</span>
                                      </span>
                                    )}
                                  </div>
                                  {headerMeta && (
                                    <p className="mt-1 text-xs text-gray-500">
                                      {headerMeta}
                                    </p>
                                  )}
                                </div>
                                <span
                                  className={`text-xs font-semibold ${
                                    isTaskOpen
                                      ? "text-sflPrimary"
                                      : "text-gray-500"
                                  }`}
                                >
                                  {isTaskOpen ? "Hide details" : "View details"}
                                </span>
                              </button>
                            </div>
                            {isTaskOpen && (
                              <div className="mt-4 space-y-4">
                                <div className="flex flex-col gap-3 md:flex-row md:items-start md:justify-between">
                                  <div className="flex-1">
                                    <label className="text-xs font-semibold uppercase tracking-wide text-gray-500">
                                      Task Title
                                    </label>
                                    <input
                                      type="text"
                                      value={task.title}
                                      onChange={(event) =>
                                        onUpdateTask(
                                          template.id,
                                          category.id,
                                          task.id,
                                          {
                                            title: event.target.value,
                                          }
                                        )
                                      }
                                      disabled={isSuperAdminMandatoryTask}
                                      className="mt-1 w-full rounded-md border border-gray-200 px-3 py-2 text-sm focus:border-sflPrimary focus:outline-none disabled:bg-gray-50 disabled:text-gray-400"
                                    />
                                  </div>
                                  <div className="flex items-center gap-2 text-xs font-semibold text-gray-700 md:self-center md:pt-6">
                                    {task.isMandatory && (
                                      <span className="inline-flex items-center gap-1 rounded-full border border-sflPrimary/40 bg-sflPrimary/10 px-2.5 py-0.5 text-[0.6875rem] font-semibold text-sflPrimary">
                                        <LockClosedIcon
                                          className="h-3.5 w-3.5"
                                          aria-hidden="true"
                                        />
                                        <span>Mandatory</span>
                                      </span>
                                    )}
                                  </div>
                                  {!isSuperAdminMandatoryTask && (
                                    <label className="flex items-center gap-2 text-xs font-semibold text-gray-700 md:self-center md:pt-6">
                                      <input
                                        type="checkbox"
                                        checked={Boolean(task.isMandatory)}
                                        onChange={(event) =>
                                          onUpdateTask(
                                            template.id,
                                            category.id,
                                            task.id,
                                            {
                                              isMandatory: event.target.checked,
                                              mandatoryBy: event.target.checked
                                                ? "orgAdmin"
                                                : null,
                                            }
                                          )
                                        }
                                        className="h-4 w-4 rounded border-gray-300 text-sflPrimary focus:ring-sflPrimary"
                                      />
                                      <span>Mandatory (Org Admin)</span>
                                    </label>
                                  )}
                                  {!isSuperAdminMandatoryTask && (
                                    <button
                                      type="button"
                                      onClick={() =>
                                        onRemoveTask(
                                          template.id,
                                          category.id,
                                          task.id
                                        )
                                      }
                                      className="inline-flex items-center justify-center rounded-md border border-red-200 px-3 py-1 text-xs font-semibold text-red-600 transition hover:bg-red-50 focus:outline-none focus:ring-2 focus:ring-red-200 focus:ring-offset-1"
                                    >
                                      Remove
                                    </button>
                                  )}
                                </div>
                                <div className="grid gap-4 md:grid-cols-2">
                                  <div className="md:col-span-2">
                                    <label className="text-xs font-semibold uppercase tracking-wide text-gray-500">
                                      Description
                                    </label>
                                    <textarea
                                      value={task.description || ""}
                                      onChange={(event) =>
                                        onUpdateTask(
                                          template.id,
                                          category.id,
                                          task.id,
                                          {
                                            description: event.target.value,
                                          }
                                        )
                                      }
                                      rows={3}
                                      disabled={isSuperAdminMandatoryTask}
                                      className="mt-1 w-full rounded-md border border-gray-200 px-3 py-2 text-sm focus:border-sflPrimary focus:outline-none disabled:bg-gray-50 disabled:text-gray-400"
                                    />
                                  </div>
                                  <div>
                                    <label className="text-xs font-semibold uppercase tracking-wide text-gray-500">
                                      Timeline
                                    </label>
                                    <select
                                      value={
                                        task.timelineType ||
                                        timelineTypes.DEFINITE
                                      }
                                      onChange={(event) =>
                                        onUpdateTask(
                                          template.id,
                                          category.id,
                                          task.id,
                                          {
                                            timelineType: event.target.value,
                                          }
                                        )
                                      }
                                      disabled={isSuperAdminMandatoryTask}
                                      className="mt-1 w-full rounded-md border border-gray-200 px-3 py-2 text-sm focus:border-sflPrimary focus:outline-none disabled:bg-gray-50 disabled:text-gray-400"
                                    >
                                      {timelineOptions.map((option) => (
                                        <option
                                          key={option.value}
                                          value={option.value}
                                        >
                                          {option.label}
                                        </option>
                                      ))}
                                    </select>
                                  </div>
                                  <div>
                                    <label className="text-xs font-semibold uppercase tracking-wide text-gray-500">
                                      Checklist Header
                                    </label>
                                    <input
                                      type="text"
                                      value={task.checklistHeader || ""}
                                      onChange={(event) =>
                                        onUpdateTask(
                                          template.id,
                                          category.id,
                                          task.id,
                                          {
                                            checklistHeader: event.target.value,
                                          }
                                        )
                                      }
                                      disabled={isSuperAdminMandatoryTask}
                                      className="mt-1 w-full rounded-md border border-gray-200 px-3 py-2 text-sm focus:border-sflPrimary focus:outline-none disabled:bg-gray-50 disabled:text-gray-400"
                                    />
                                  </div>
                                  {/* Scheduling fields hidden in Org Admin template editor */}
                                </div>
                              </div>
                            )}
                          </div>
                        );
                      })
                    )}
                  </div>
                  <div className="flex justify-between items-center">
                    {!hasSuperAdminMandatoryTask && (
                      <button
                        type="button"
                        onClick={() =>
                          onRemoveCategory(template.id, category.id)
                        }
                        className="inline-flex items-center justify-center rounded-md border border-red-200 px-3 py-1 text-xs font-semibold text-red-600 transition hover:bg-red-50 focus:outline-none focus:ring-2 focus:ring-red-500 focus:ring-offset-2"
                      >
                        Remove Category
                      </button>
                    )}
                    <button
                      type="button"
                      onClick={() => handleAddTask(category.id)}
                      className="inline-flex items-center justify-center rounded-md border border-sflPrimary px-3 py-1 text-xs font-semibold text-sflPrimary transition hover:bg-sflPrimary/10 focus:outline-none focus:ring-2 focus:ring-sflPrimary focus:ring-offset-2"
                    >
                      + Add Task
                    </button>
                  </div>
                </div>
              )}
            </div>
          );
        })}
      </div>
    </section>
  );
};
TemplateEditor.propTypes = {
  template: PropTypes.object.isRequired,
  frequencyOptions: PropTypes.array.isRequired,
  timelineTypes: PropTypes.object.isRequired,
  onUpdateCategory: PropTypes.func.isRequired,
  onAddCategory: PropTypes.func.isRequired,
  onRemoveCategory: PropTypes.func.isRequired,
  onUpdateTask: PropTypes.func.isRequired,
  onAddTask: PropTypes.func.isRequired,
  onRemoveTask: PropTypes.func.isRequired,
};
const SettingsView = ({
  templates,
  selectedTemplateId,
  onViewTemplate,
  onEditTemplate,
  onCloseTemplate,
  selectedTemplate,
  templatePanelMode,
  frequencyOptions,
  timelineTypes,
  onUpdateCategory,
  onAddCategory,
  onRemoveCategory,
  onUpdateTask,
  onAddTask,
  onRemoveTask,
}) => (
  <div className="rounded-xl border border-gray-200 p-4">
    <div className="mb-6">
      <h3 className="text-lg font-semibold text-gray-800">
        Compliance Settings
      </h3>
      <p className="text-sm text-gray-600">
        Manage categories and tasks for your compliance template.
      </p>
    </div>
    {templates?.length > 0 ? (
      <TemplatesTable
        templates={templates}
        selectedTemplateId={selectedTemplateId}
        onView={onViewTemplate}
        onEdit={onEditTemplate}
        onClose={onCloseTemplate}
        templatePanelMode={templatePanelMode}
        selectedTemplate={selectedTemplate}
        frequencyOptions={frequencyOptions}
        timelineTypes={timelineTypes}
        onUpdateCategory={onUpdateCategory}
        onAddCategory={onAddCategory}
        onRemoveCategory={onRemoveCategory}
        onUpdateTask={onUpdateTask}
        onAddTask={onAddTask}
        onRemoveTask={onRemoveTask}
        canDelete={false}
        TemplateEditor={TemplateEditor}
      />
    ) : (
      <div className="flex h-32 flex-col items-center justify-center rounded-lg border border-dashed border-gray-300 bg-gray-50 p-6 text-center">
        <p className="text-sm font-medium text-gray-900">
          No active template found
        </p>
        <p className="mt-1 text-sm text-gray-500">
          An active template is required to manage compliance settings.
        </p>
      </div>
    )}
    {/* Sanction List Management - View Only for Org Admins */}
    <div className="mt-8 border-t border-gray-200 pt-8">
      <SanctionListManager isViewOnly={true} />
    </div>
  </div>
);
SettingsView.propTypes = {
  templates: PropTypes.array,
  selectedTemplateId: PropTypes.string,
  onViewTemplate: PropTypes.func.isRequired,
  onEditTemplate: PropTypes.func.isRequired,
  onCloseTemplate: PropTypes.func.isRequired,
  selectedTemplate: PropTypes.object,
  templatePanelMode: PropTypes.string,
  frequencyOptions: PropTypes.array.isRequired,
  timelineTypes: PropTypes.object.isRequired,
  onUpdateCategory: PropTypes.func.isRequired,
  onAddCategory: PropTypes.func.isRequired,
  onRemoveCategory: PropTypes.func.isRequired,
  onUpdateTask: PropTypes.func.isRequired,
  onAddTask: PropTypes.func.isRequired,
  onRemoveTask: PropTypes.func.isRequired,
};
const UserCompliancePanel = ({
  userProfile,
  normalizedView,
  template,
  templates,
  selectedTemplateId,
  onViewTemplate,
  onEditTemplate,
  onCloseTemplate,
  selectedTemplate,
  templatePanelMode,
  history,
  trackerEntities,
  frequencyOptions,
  timelineTypes,
  onUpdateCategory,
  onAddCategory,
  onRemoveCategory,
  onUpdateTask,
  onAddTask,
  onRemoveTask,
  orgAdminIdentifier,
}) => (
  <section className="space-y-6 rounded-2xl border border-gray-200 bg-white p-6 shadow-sm">
    {normalizedView === "summary" && (
      <SummaryView
        userProfile={userProfile}
        template={template}
        history={history}
        trackerEntities={trackerEntities}
      />
    )}
    {normalizedView === "tracker" && (
      <div className="space-y-6">
        <div className="rounded-xl border border-gray-200 p-4">
          <h3 className="text-lg font-semibold text-gray-800">
            Compliance Tracker
          </h3>
          <p className="text-sm text-gray-600">
            Your compliance deadlines and task status.
          </p>
          <div className="mt-4">
            <ComplianceTrackerTable
              entities={trackerEntities}
              formatDate={formatDateDisplay}
              maxTaskSelections={12}
            />
          </div>
        </div>
      </div>
    )}
    {normalizedView === "analysis" && (
      <OrgComplianceTasks orgAdminId={orgAdminIdentifier} />
    )}
    {normalizedView === "settings" && (
      <SettingsView
        templates={templates}
        selectedTemplateId={selectedTemplateId}
        onViewTemplate={onViewTemplate}
        onEditTemplate={onEditTemplate}
        onCloseTemplate={onCloseTemplate}
        selectedTemplate={selectedTemplate}
        templatePanelMode={templatePanelMode}
        frequencyOptions={frequencyOptions}
        timelineTypes={timelineTypes}
        onUpdateCategory={onUpdateCategory}
        onAddCategory={onAddCategory}
        onRemoveCategory={onRemoveCategory}
        onUpdateTask={onUpdateTask}
        onAddTask={onAddTask}
        onRemoveTask={onRemoveTask}
      />
    )}
  </section>
);
UserCompliancePanel.propTypes = {
  userProfile: PropTypes.object.isRequired,
  normalizedView: PropTypes.oneOf([...ALLOWED_VIEWS]).isRequired,
  template: PropTypes.object,
  templates: PropTypes.array,
  selectedTemplateId: PropTypes.string,
  onViewTemplate: PropTypes.func.isRequired,
  onEditTemplate: PropTypes.func.isRequired,
  onCloseTemplate: PropTypes.func.isRequired,
  selectedTemplate: PropTypes.object,
  templatePanelMode: PropTypes.string,
  history: PropTypes.array.isRequired,
  trackerEntities: PropTypes.array.isRequired,
  frequencyOptions: PropTypes.array.isRequired,
  timelineTypes: PropTypes.object.isRequired,
  onUpdateCategory: PropTypes.func.isRequired,
  onAddCategory: PropTypes.func.isRequired,
  onRemoveCategory: PropTypes.func.isRequired,
  onUpdateTask: PropTypes.func.isRequired,
  onAddTask: PropTypes.func.isRequired,
  onRemoveTask: PropTypes.func.isRequired,
  orgAdminIdentifier: PropTypes.string,
};
UserCompliancePanel.defaultProps = {
  template: undefined,
};
const OrgComplianceDashboard = ({ activeView, organization }) => {
  const {
    corporateSummaries = [],
    companySummaries = [],
    templateHistory = [],
    templates = [],
    templateAssignments,
    listCorporateTasks,
    listCompanyTasks,
    orgAdminProfile,
    orgAdminSummaryProfile,
    FREQUENCY_OPTIONS,
    TIMELINE_TYPES,
    updateTemplateCategory,
    addTemplateCategory,
    removeTemplateCategory,
    addTemplateTask,
    orgAdmins,
    updateTemplateTask,
    removeTemplateTask,
  } = useComplianceContext();
  const dispatch = useDispatch();
  const { corporates } = useSelector(
    (state) => state.corporatesForDashboardSlice
  );
  useEffect(() => {
    if (organization?._id) {
      dispatch(
        getCorporatesForDashboard({
          companyRegNumber: organization.names?.companyRegNumber,
          companyDashboard: !organization.partnershipType,
          limit: 100,
          skip: 0,
          order: "asc",
        })
      );
    }
  }, [dispatch, organization]);
  const [selectedTemplateId, setSelectedTemplateId] = useState(null);
  const [templatePanelMode, setTemplatePanelMode] = useState("edit");
  const [hasManualTemplateSelection, setHasManualTemplateSelection] =
    useState(false);
  const [resolvedOrgAdminUserId, setResolvedOrgAdminUserId] = useState(null);
  const organizationName = organization?.names?.companyName || "Organization";
  const orgAdminDisplayName = useMemo(() => {
    return (
      orgAdminProfile?.name || orgAdminSummaryProfile?.name || organizationName
    );
  }, [orgAdminProfile, orgAdminSummaryProfile, organizationName]);

  const currentOrgId = useMemo(() => {
    return (
      organization?._id ||
      orgAdminProfile?.corporateId ||
      orgAdminSummaryProfile?.corporateId ||
      null
    );
  }, [organization, orgAdminProfile, orgAdminSummaryProfile]);

  const handleUpdateCategory = useCallback(
    (templateId, categoryId, updates) => {
      updateTemplateCategory(templateId, categoryId, updates, {
        updatedBy: orgAdminDisplayName,
      });
    },
    [updateTemplateCategory, orgAdminDisplayName]
  );
  const handleAddCategory = useCallback(
    (templateId) => {
      return addTemplateCategory(
        templateId,
        {},
        { updatedBy: orgAdminDisplayName }
      );
    },
    [addTemplateCategory, orgAdminDisplayName]
  );
  const handleRemoveCategory = useCallback(
    (templateId, categoryId) => {
      removeTemplateCategory(templateId, categoryId, {
        updatedBy: orgAdminDisplayName,
      });
    },
    [removeTemplateCategory, orgAdminDisplayName]
  );
  const handleUpdateTask = useCallback(
    (templateId, categoryId, taskId, updates) => {
      updateTemplateTask(templateId, categoryId, taskId, updates, {
        updatedBy: orgAdminDisplayName,
      });
    },
    [updateTemplateTask, orgAdminDisplayName]
  );
  const handleAddTask = useCallback(
    (templateId, categoryId, taskPayload) => {
      addTemplateTask(templateId, categoryId, taskPayload, {
        updatedBy: orgAdminDisplayName,
      });
    },
    [addTemplateTask, orgAdminDisplayName]
  );
  const handleRemoveTask = useCallback(
    (templateId, categoryId, taskId) => {
      removeTemplateTask(templateId, categoryId, taskId, {
        updatedBy: orgAdminDisplayName,
      });
    },
    [removeTemplateTask, orgAdminDisplayName]
  );
  const handleViewTemplate = useCallback((templateId) => {
    if (!templateId) return;
    setSelectedTemplateId(templateId);
    setTemplatePanelMode("view");
    setHasManualTemplateSelection(true);
  }, []);
  const handleEditTemplate = useCallback((templateId) => {
    if (!templateId) return;
    setSelectedTemplateId(templateId);
    setTemplatePanelMode("edit");
    setHasManualTemplateSelection(true);
  }, []);
  const handleCloseTemplate = useCallback(() => {
    setSelectedTemplateId(null);
    setTemplatePanelMode("edit");
    setHasManualTemplateSelection(true);
  }, []);
  const selectedTemplate = useMemo(() => {
    if (!selectedTemplateId) return null;
    return templates.find((t) => t.id === selectedTemplateId) || null;
  }, [selectedTemplateId, templates]);
  const normalizedView = useMemo(
    () => (ALLOWED_VIEWS.has(activeView) ? activeView : "summary"),
    [activeView]
  );
  // Build user profile from orgAdminSummaryProfile
  const userProfile = useMemo(() => {
    if (!orgAdminSummaryProfile) {
      return {
        code: organizationName,
        tasks: [],
        summary: buildEmptySummary(),
        corporateIds: [],
        companyIds: [],
      };
    }
    const tasks = orgAdminSummaryProfile.tasks || [];
    const summary =
      orgAdminSummaryProfile.summary || computeSummaryFromTasks(tasks);
    return {
      ...orgAdminSummaryProfile,
      tasks,
      summary,
      code: orgAdminSummaryProfile.code || organizationName,
    };
  }, [orgAdminSummaryProfile, organizationName]);

  const orgAdminTemplates = useMemo(() => {
    if (!orgAdmins || orgAdmins.length === 0 || !currentOrgId) return [];
    const orgId = String(currentOrgId);

    const templateIds = new Set(
      orgAdmins
        .filter(
          (admin) => admin.templateId && String(admin.organizationId) === orgId
        )
        .map((admin) => String(admin.templateId))
    );

    if (!templateIds.size) return [];

    return templates.filter((template) => templateIds.has(String(template.id)));
  }, [orgAdmins, currentOrgId, templates]);
  useEffect(() => {
    if (!orgAdminProfile && userProfile.corporateIds?.length > 0) {
      const firstCorporateId = userProfile.corporateIds[0];
      if (firstCorporateId && !resolvedOrgAdminUserId) {
        complianceService
          .getOrgAdminByCorporate(firstCorporateId)
          .then((response) => {
            const orgAdmin = response?.data?.orgAdmin;
            if (orgAdmin && response?.data?.exists) {
              const userId = orgAdmin.userId || orgAdmin.id;
              if (userId) {
                setResolvedOrgAdminUserId(userId);
              }
            }
          })
          .catch((error) => {
            console.error(
              "[OrgComplianceDashboard] Failed to resolve org admin:",
              error
            );
          });
      }
    } else if (orgAdminProfile) {
      setResolvedOrgAdminUserId(null);
    }
  }, [orgAdminProfile, userProfile, resolvedOrgAdminUserId]);
  // Build orgAdminIdentifier
  const orgAdminIdentifier = useMemo(() => {
    let identifier =
      orgAdminProfile?.id ||
      orgAdminProfile?._id ||
      orgAdminProfile?.userId ||
      orgAdminSummaryProfile?.id ||
      resolvedOrgAdminUserId ||
      organization?.id ||
      organization?._id;
    return identifier;
  }, [
    orgAdminProfile,
    orgAdminSummaryProfile,
    resolvedOrgAdminUserId,
    organization,
  ]);
  const userTemplate = useMemo(() => {
    if (orgAdminTemplates.length > 0) {
      return orgAdminTemplates[0];
    }
    return undefined;
  }, [orgAdminTemplates]);
  // Get user's template history
  const userHistory = useMemo(() => {
    const corporateIds = userProfile.corporateIds || [];
    const companyIds = userProfile.companyIds || [];
    return templateHistory
      .filter((entry) => {
        if (entry.scope === "corporate") {
          return corporateIds.includes(entry.scopeId);
        }
        if (entry.scope === "company") {
          return companyIds.includes(entry.scopeId);
        }
        return false;
      })
      .sort((a, b) => new Date(b.appliedAt) - new Date(a.appliedAt));
  }, [userProfile, templateHistory]);
  const trackerEntities = useMemo(() => {
    if (!userTemplate) {
      return [];
    }
    return buildTrackerEntities(
      userProfile,
      corporateSummaries,
      companySummaries,
      listCorporateTasks,
      listCompanyTasks,
      corporates?.items || [],
      userTemplate
    );
  }, [
    userTemplate,
    userProfile,
    corporateSummaries,
    companySummaries,
    listCorporateTasks,
    listCompanyTasks,
    corporates?.items,
  ]);
  if (!userProfile) {
    return (
      <div className="rounded-xl border border-gray-200 bg-white p-6 text-center text-sm text-gray-600">
        Loading compliance data...
      </div>
    );
  }
  return (
    <div className="space-y-8">
      <UserCompliancePanel
        userProfile={userProfile}
        normalizedView={normalizedView}
        template={userTemplate}
        templates={orgAdminTemplates}
        selectedTemplateId={selectedTemplateId}
        onViewTemplate={handleViewTemplate}
        onEditTemplate={handleEditTemplate}
        onCloseTemplate={handleCloseTemplate}
        selectedTemplate={selectedTemplate}
        templatePanelMode={templatePanelMode}
        history={userHistory}
        trackerEntities={trackerEntities}
        frequencyOptions={FREQUENCY_OPTIONS}
        timelineTypes={TIMELINE_TYPES}
        onUpdateCategory={handleUpdateCategory}
        onAddCategory={handleAddCategory}
        onRemoveCategory={handleRemoveCategory}
        onUpdateTask={handleUpdateTask}
        onAddTask={handleAddTask}
        onRemoveTask={handleRemoveTask}
        orgAdminIdentifier={orgAdminIdentifier}
      />
    </div>
  );
};
OrgComplianceDashboard.propTypes = {
  activeView: PropTypes.oneOf([...ALLOWED_VIEWS]),
};
OrgComplianceDashboard.defaultProps = {
  activeView: "summary",
};
export default OrgComplianceDashboard;
